import type { UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import ITreeNode from '../tree/ITreeNode';

export interface FlattenedTreeNode extends ITreeNode {
  depth: number;
  index: number;
}

/**
 * Calculate drag depth based on horizontal offset
 */
function getDragDepth(offset: number, indentationWidth: number): number {
  const THRESHOLD_RIGHT = 50;
  const THRESHOLD_LEFT = -10;

  if (offset >= THRESHOLD_RIGHT) {
    return Math.floor(offset / indentationWidth);
  }
  if (offset <= THRESHOLD_LEFT) {
    return -1 * Math.ceil(Math.abs(offset) / 10);
  }

  return 0;
}

/**
 * Calculate the projected depth and parent for a dragged item
 */
export function getProjection(
  items: FlattenedTreeNode[],
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier,
  dragOffset: number,
  indentationWidth: number
) {
  const overItemIndex = items.findIndex(({ id }) => id === overId);
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);
  const activeItem = items[activeItemIndex];
  
  if (!activeItem) return null;
  
  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];
  const dragDepth = getDragDepth(dragOffset, indentationWidth);
  const projectedDepth = activeItem.depth + dragDepth;
  const maxDepth = getMaxDepth({ previousItem });
  const minDepth = getMinDepth({ nextItem });
  
  let depth = projectedDepth;

  if (projectedDepth >= maxDepth) {
    depth = maxDepth;
  } else if (projectedDepth < minDepth) {
    depth = minDepth;
  }

  const parentId = getParentId();
  
  // Prevent dropping as children of default pages
  if (parentId !== null) {
    const parentItem = items.find(item => item.id === parentId);
    if (parentItem && (parentItem.data.type === 'default' || parentItem.data.type === 'Startseite')) {
      return null;
    }
  }

  return { depth, maxDepth, minDepth, parentId };

  function getParentId(): UniqueIdentifier | null {
    if (depth === 0 || !previousItem) {
      return null;
    }

    if (depth === previousItem.depth) {
      return previousItem.parent;
    }

    if (depth > previousItem.depth) {
      return previousItem.id;
    }

    const newParent = newItems
      .slice(0, overItemIndex)
      .reverse()
      .find((item) => item.depth === depth)?.parent;

    return newParent ?? null;
  }
}

/**
 * Get maximum allowed depth (one level deeper than previous item)
 */
function getMaxDepth({ previousItem }: { previousItem?: FlattenedTreeNode }): number {
  if (previousItem) {
    return previousItem.depth + 1;
  }
  return 0;
}

/**
 * Get minimum allowed depth (always 0 - root level)
 * Items can always be moved to root level or any level above
 */
function getMinDepth({ nextItem }: { nextItem?: FlattenedTreeNode }): number {
  // Always allow moving to root level (depth 0)
  return 0;
}

/**
 * Flatten tree structure into array with depth information
 */
function flatten(
  items: ITreeNode[],
  allNodes: ITreeNode[],
  parentId: UniqueIdentifier | null = null,
  depth = 0
): FlattenedTreeNode[] {
  return items.reduce<FlattenedTreeNode[]>((acc, item, index) => {
    const children = allNodes.filter(
      (n) => n.parent === item.id && (n.data.origin === 'internal' || n.data.origin === 'custom')
    );
    
    return [
      ...acc,
      { ...item, depth, index },
      ...flatten(children, allNodes, item.id, depth + 1),
    ];
  }, []);
}

/**
 * Convert tree to flattened array
 */
export function flattenTree(rootNodes: ITreeNode[], allNodes: ITreeNode[]): FlattenedTreeNode[] {
  return flatten(rootNodes, allNodes);
}

/**
 * Remove children of collapsed or dragged items
 */
export function removeChildrenOf(
  items: FlattenedTreeNode[],
  ids: UniqueIdentifier[]
): FlattenedTreeNode[] {
  const excludeParentIds = [...ids];

  return items.filter((item) => {
    if (item.parent && excludeParentIds.includes(item.parent)) {
      const children = items.filter((n) => n.parent === item.id);
      if (children.length > 0) {
        excludeParentIds.push(item.id);
      }
      return false;
    }
    return true;
  });
}

/**
 * Count total children of an item
 */
export function getChildCount(allNodes: ITreeNode[], id: UniqueIdentifier): number {
  const children = allNodes.filter((n) => n.parent === id);
  
  return children.reduce((count, child) => {
    return count + 1 + getChildCount(allNodes, child.id);
  }, 0);
}
